import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocumentEditor from "@/components/DocumentEditor";
import CommentsPanel from "@/components/CommentsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Star, StarOff, Trash2, History, Link2, Share2, Plus,
  FolderKanban, FileText, ChevronRight, X, Mountain, Puzzle, Ticket, Rocket,
} from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { v: "general", l: "General" }, { v: "project_doc", l: "Project Doc" },
  { v: "technical_doc", l: "Technical" }, { v: "requirement", l: "Requirement" },
  { v: "sop", l: "SOP" }, { v: "meeting_notes", l: "Meeting Notes" },
  { v: "decision", l: "Decision" }, { v: "release_notes", l: "Release Notes" },
  { v: "cr_doc", l: "CR Doc" }, { v: "knowledge_base", l: "Knowledge Base" }, { v: "policy", l: "Policy" },
];
const STATUSES = [
  { v: "draft", l: "Draft" }, { v: "in_review", l: "In Review" },
  { v: "published", l: "Published" }, { v: "archived", l: "Archived" },
];
const VISIBILITIES = [
  { v: "private", l: "Private — only you" },
  { v: "team", l: "Team" },
  { v: "project", l: "Project members" },
  { v: "workspace", l: "Everyone in workspace" },
];
const ENTITY_META = {
  project: { icon: FolderKanban, label: "Project" },
  epic: { icon: Mountain, label: "Epic" },
  feature: { icon: Puzzle, label: "Feature" },
  task: { icon: FileText, label: "Task" },
  cr: { icon: Ticket, label: "CR" },
  release: { icon: Rocket, label: "Release" },
};
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function LinkEntityDialog({ open, onOpenChange, docId, projectId, onSaved }) {
  const [etype, setEtype] = useState("project");
  const [options, setOptions] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    const load = async () => {
      try {
        if (etype === "project") setOptions((await api.get("/projects")).data);
        else if (etype === "task") setOptions((await api.get(`/tasks${projectId ? `?project_id=${projectId}` : ""}`)).data);
        else if (etype === "cr") setOptions((await api.get(`/tickets${projectId ? `?project_id=${projectId}` : ""}`)).data);
        else if (etype === "epic" && projectId) setOptions((await api.get(`/projects/${projectId}/epics`)).data);
        else if (etype === "feature" && projectId) setOptions((await api.get(`/projects/${projectId}/features`)).data);
        else setOptions([]);
      } catch { setOptions([]); }
    };
    load();
  }, [open, etype, projectId]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return options.slice(0, 60);
    return options.filter((o) =>
      (o.title || o.name || o.code || "").toLowerCase().includes(needle) ||
      (o.identifier || "").toLowerCase().includes(needle)
    ).slice(0, 60);
  }, [options, q]);

  const link = async (entity_id) => {
    setSaving(true);
    try {
      await api.post(`/documents/${docId}/links`, { entity_type: etype, entity_id, relation_type: "related" });
      toast.success("Linked");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="doc-link-dialog">
        <DialogHeader>
          <DialogTitle>Link a PRIZM object</DialogTitle>
          <DialogDescription>Connect this document to a Project, Epic, Feature, Task or CR for a knowledge graph that stays in sync.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Select value={etype} onValueChange={setEtype}>
            <SelectTrigger className="w-40" data-testid="link-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["project", "epic", "feature", "task", "cr"].map((e) => (
                <SelectItem key={e} value={e}>{ENTITY_META[e]?.label || e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="Search by name / code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-search" />
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1 mt-2">
          {filtered.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No matches.</div>}
          {filtered.map((o) => {
            const name = o.title || o.name || o.code || o.identifier || o.id;
            const sub = o.identifier || o.code || "";
            return (
              <button
                key={o.id}
                disabled={saving}
                onClick={() => link(o.id)}
                data-testid={`link-pick-${o.id}`}
                className="w-full glass rounded-md px-3 py-2 flex items-center gap-2 hover:border-primary/40 text-left"
              >
                <span className="flex-1 min-w-0 text-sm truncate">{name}</span>
                {sub && <Badge variant="outline" className="text-[10px] font-mono">{sub}</Badge>}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkRow({ link, onRemove, canEdit }) {
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    const paths = {
      project: `/projects/${link.entity_id}`,
      epic: `/epics/${link.entity_id}`,
      feature: `/features/${link.entity_id}`,
      task: `/tasks/${link.entity_id}`,
      cr: `/tickets/${link.entity_id}`,
    };
    const api_path = {
      project: `/projects/${link.entity_id}`,
      epic: `/epics/${link.entity_id}`,
      feature: `/features/${link.entity_id}`,
      task: `/tasks/${link.entity_id}`,
      cr: `/tickets/${link.entity_id}`,
    }[link.entity_type];
    if (!api_path) return;
    api.get(api_path).then((r) => {
      const d = r.data;
      setResolved({
        name: d.title || d.name || d.code || d.identifier || link.entity_id,
        sub: d.identifier || d.code || "",
        href: paths[link.entity_type],
      });
    }).catch(() => setResolved({ name: link.entity_id, sub: "", href: paths[link.entity_type] }));
  }, [link]);
  const Meta = ENTITY_META[link.entity_type] || { icon: Link2, label: link.entity_type };
  return (
    <div className="glass rounded-md px-3 py-2 flex items-center gap-2" data-testid={`doc-link-${link.id}`}>
      <Meta.icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <RouterLink to={resolved?.href || "#"} className="flex-1 min-w-0 text-sm hover:text-primary truncate">
        {resolved?.name || "Loading…"}
      </RouterLink>
      {resolved?.sub && <Badge variant="outline" className="text-[10px] font-mono">{resolved.sub}</Badge>}
      <Badge variant="outline" className="text-[9px] capitalize">{Meta.label}</Badge>
      {canEdit && (
        <Button size="icon" variant="ghost" onClick={() => onRemove(link.id)} className="h-6 w-6">
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function VersionsDialog({ open, onOpenChange, docId, onRestored }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => { if (open) api.get(`/documents/${docId}/versions`).then((r) => setVersions(r.data)); }, [open, docId]);
  const restore = async (v) => {
    if (!window.confirm(`Restore to v${v.version}? Your current content is saved as the newest version.`)) return;
    try { await api.post(`/documents/${docId}/versions/${v.version}/restore`); toast.success(`Restored to v${v.version}`); onRestored?.(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="doc-versions-dialog">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>Every content change creates a version. Restore to roll back.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {versions.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No versions yet — edit the doc to create one.</div>}
          {versions.map((v) => (
            <div key={v.id} className="glass rounded-md px-3 py-2 flex items-center gap-2 text-sm">
              <span className="font-mono text-xs text-muted-foreground">v{v.version}</span>
              <span className="flex-1 min-w-0 truncate">{v.title}</span>
              <span className="text-[10px] text-muted-foreground">{(v.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <Button size="sm" variant="outline" onClick={() => restore(v)}>Restore</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [title, setTitle] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const titleTimer = useRef(null);

  const load = () => api.get(`/documents/${id}`).then((r) => { setDoc(r.data); setTitle(r.data.title); });
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
  }, [id]);

  if (!doc) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const canEdit = user?.role === "admin" || doc.owner_id === user?.id;
  const owner = employees.find((e) => e.user_id === doc.owner_id || e.id === doc.owner_id);

  const saveContent = async (json) => {
    try { await api.patch(`/documents/${id}`, { content: json }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Autosave failed"); }
  };
  const saveField = async (patch) => {
    try { const { data } = await api.patch(`/documents/${id}`, patch); setDoc((d) => ({ ...d, ...data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onTitleChange = (v) => {
    setTitle(v);
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(() => saveField({ title: v }), 800);
  };
  const toggleFav = async () => {
    try {
      if (doc.is_favorite) await api.delete(`/documents/${id}/favorite`);
      else await api.post(`/documents/${id}/favorite`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeLink = async (link_id) => {
    try { await api.delete(`/documents/${id}/links/${link_id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const trash = async () => {
    if (!window.confirm("Move this document to Trash?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Moved to Trash"); nav("/documentation?tab=trash"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 max-w-7xl">
      <div className="min-w-0 space-y-4">
        <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => nav("/documentation")} data-testid="doc-back"><ArrowLeft className="w-3.5 h-3.5 mr-1" />Documentation</Button>
          {(doc.breadcrumbs || []).map((c) => (
            <span key={c.id} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              <RouterLink to={`/documentation/${c.id}`} className="hover:text-primary">{c.icon || "📄"} {c.title}</RouterLink>
            </span>
          ))}
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground truncate">{doc.title}</span>
        </div>

        <div className="glass rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              disabled={!canEdit}
              data-testid="doc-title"
              className="flex-1 text-2xl font-display font-semibold border-0 bg-transparent px-0 h-auto focus-visible:ring-0 min-w-0"
              placeholder="Untitled"
            />
            <Button size="sm" variant="ghost" onClick={toggleFav} data-testid="doc-fav" title={doc.is_favorite ? "Unfavorite" : "Favorite"}>
              {doc.is_favorite ? <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> : <StarOff className="w-4 h-4" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowVersions(true)} data-testid="doc-versions-btn"><History className="w-4 h-4 mr-1" />History</Button>
            {canEdit && <Button size="sm" variant="outline" onClick={trash} data-testid="doc-trash"><Trash2 className="w-4 h-4 mr-1 text-red-500" />Trash</Button>}
          </div>

          <div className="flex items-center gap-2 flex-wrap pb-3 border-b border-border/40">
            <Select value={doc.doc_type} onValueChange={(v) => canEdit && saveField({ doc_type: v })} disabled={!canEdit}>
              <SelectTrigger className="w-40 h-7 text-xs" data-testid="doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={doc.status} onValueChange={(v) => canEdit && saveField({ status: v })} disabled={!canEdit}>
              <SelectTrigger className="w-32 h-7 text-xs" data-testid="doc-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={doc.visibility} onValueChange={(v) => canEdit && saveField({ visibility: v })} disabled={!canEdit}>
              <SelectTrigger className="w-48 h-7 text-xs" data-testid="doc-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>{VISIBILITIES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
            {owner && (
              <div className="flex items-center gap-1 ml-auto text-[11px] text-muted-foreground">
                <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>
                {owner.full_name}
              </div>
            )}
          </div>

          <DocumentEditor
            content={doc.content}
            onChange={saveContent}
            readOnly={!canEdit}
          />
        </div>
      </div>

      <aside className="space-y-4">
        <div className="glass rounded-xl p-4" data-testid="doc-links-card">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4" />Linked PRIZM Objects</div>
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={() => setShowLink(true)} data-testid="doc-link-add" className="h-7 px-2">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {(doc.links || []).length === 0 ? (
            <div className="text-xs text-muted-foreground py-4 text-center">No links yet. Attach this doc to a Project, Task or CR.</div>
          ) : (
            <div className="space-y-1">
              {doc.links.map((l) => <LinkRow key={l.id} link={l} onRemove={removeLink} canEdit={canEdit} />)}
            </div>
          )}
        </div>

        {(doc.children || []).length > 0 && (
          <div className="glass rounded-xl p-4" data-testid="doc-children-card">
            <div className="font-medium text-sm mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4" />Nested documents</div>
            <div className="space-y-1">
              {doc.children.map((c) => (
                <RouterLink key={c.id} to={`/documentation/${c.id}`} className="glass rounded-md px-3 py-1.5 flex items-center gap-2 text-xs hover:border-primary/40">
                  <span>{c.icon || "📄"}</span>
                  <span className="flex-1 min-w-0 truncate">{c.title}</span>
                </RouterLink>
              ))}
            </div>
          </div>
        )}

        <CommentsPanel docId={id} />

        <div className="glass rounded-xl p-4 text-[11px] text-muted-foreground space-y-1">
          <div><b className="text-foreground">Created</b> {(doc.created_at || "").slice(0, 10)}</div>
          <div><b className="text-foreground">Updated</b> {(doc.updated_at || "").slice(0, 10)}</div>
        </div>
      </aside>

      <LinkEntityDialog open={showLink} onOpenChange={setShowLink} docId={id} projectId={doc.project_id} onSaved={load} />
      <VersionsDialog open={showVersions} onOpenChange={setShowVersions} docId={id} onRestored={load} />
    </div>
  );
}
